import { BoardMessage, BoardMessageType, MarkedCell, MarkedCellType } from '@puzzlio/core';
import {
  CellValue,
  SolutionGrid,
  Grid,
  BLACK_CELL,
  edgeName,
  getEdges,
  hasEdgeNote,
  EDGE_UP,
  EDGE_RIGHT,
  EDGE_DOWN,
  EDGE_LEFT,
} from '../types';

const DIRS = [
  [EDGE_UP, -1, 0],
  [EDGE_RIGHT, 0, 1],
  [EDGE_DOWN, 1, 0],
  [EDGE_LEFT, 0, -1],
] as const;

/**
 * Returns true when every cell that has any edges has exactly two edges and all
 * such cells form a single connected component — i.e. the drawn path is a
 * single closed loop (though it may not cover every white cell).
 */
export function boardHasSingleContinuousLoop(grid: Grid, cells: CellValue[][]): boolean {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;

  // Collect active cells; bail early if any has ≠ 2 edges.
  const activeCells: [number, number][] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (grid[r][c] === BLACK_CELL) continue;
      const edges = getEdges(cells[r][c]);
      const edgeCount = DIRS.filter(([d]) => edges & d).length;
      if (edgeCount === 0) continue;
      if (edgeCount !== 2) return false;
      activeCells.push([r, c]);
    }
  }

  if (activeCells.length === 0) return false;

  // BFS to check all active cells are one connected component.
  const key = (r: number, c: number) => `${r},${c}`;
  const visited = new Set<string>();
  const queue: [number, number][] = [activeCells[0]];
  visited.add(key(activeCells[0][0], activeCells[0][1]));

  while (queue.length > 0) {
    const [r, c] = queue.shift()!;
    const edges = getEdges(cells[r][c]);
    for (const [dir, dr, dc] of DIRS) {
      if (!(edges & dir)) continue;
      const k = key(r + dr, c + dc);
      if (!visited.has(k)) {
        visited.add(k);
        queue.push([r + dr, c + dc]);
      }
    }
  }

  return visited.size === activeCells.length;
}

export function countCells(cells: CellValue[][], grid: Grid): { empty: number; nonEmpty: number } {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;
  let empty = 0,
    nonEmpty = 0;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (grid[row][col] === BLACK_CELL) continue;
      else if (getEdges(cells[row][col]) === 0) empty += 1;
      else nonEmpty += 1;
    }
  }
  return { empty, nonEmpty };
}

const mkErrorCell = (row: number, col: number) => ({ row, col, markType: MarkedCellType.error });
const mkErrorMessage = (message: string, markedCells: MarkedCell[]) => [
  { message, markedCells, type: BoardMessageType.error },
];

/**
 * Validate the player's board. Returns messages describing structural errors
 * (branches, dead ends) with marked cells, or a single "Incorrect" message
 * when the structure is valid but the river or the ✕ notes don't match the
 * unique solution.
 */
export function checkBoard(cells: CellValue[][], solution: SolutionGrid, grid: Grid): BoardMessage[] {
  const rows = cells.length;
  const cols = cells[0]?.length ?? 0;

  const threeEdgeCells: MarkedCell[] = [];
  const fourEdgeCells: MarkedCell[] = [];
  const incorrectCells: MarkedCell[] = [];
  const incorrectNotes: MarkedCell[] = [];
  let totalWhiteCells = 0;
  let totalEdgeCells = 0;

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (grid[row][col] === BLACK_CELL) continue;
      totalWhiteCells++;

      const edges = getEdges(cells[row][col]);
      const count = [1, 2, 4, 8].filter((d) => edges & d).length;
      if (count > 0) totalEdgeCells++;
      // A ✕ on a side the river takes. The river only crosses sides between two open cells, and a
      // ✕ there is recorded on both, so looking right and down finds each one once.
      for (const dir of [EDGE_RIGHT, EDGE_DOWN]) {
        if (hasEdgeNote(cells[row][col], dir) && solution[row][col] & dir) {
          incorrectNotes.push({ row, col, markType: MarkedCellType.error, markData: edgeName(dir) });
        }
      }
      if (count == 1) {
        // if a cell has only one edge configured and it's correct, don't consider the cell
        // incorrect, it's just incomplete
        if ((edges & solution[row][col]) !== edges) incorrectCells.push(mkErrorCell(row, col));
      } else if (count == 2) {
        if (edges !== solution[row][col]) incorrectCells.push(mkErrorCell(row, col));
      } else if (count == 3) {
        threeEdgeCells.push(mkErrorCell(row, col));
      } else if (count == 4) {
        fourEdgeCells.push(mkErrorCell(row, col));
      }
    }
  }

  if (threeEdgeCells.length > 0) {
    return mkErrorMessage('The river should never branch.', threeEdgeCells);
  }
  if (fourEdgeCells.length > 0) {
    return mkErrorMessage('The river cannot intersect itself.', fourEdgeCells);
  }

  // Check for a closed sub-loop that doesn't cover the whole board. BFS over connected components
  // of drawn edges; if every cell in a component has exactly 2 edges it forms a closed loop — flag
  // it if it's smaller than the full board.
  const visited = new Set<string>();
  const cellKey = (r: number, c: number) => `${r},${c}`;

  for (let startRow = 0; startRow < rows; startRow++) {
    for (let startCol = 0; startCol < cols; startCol++) {
      if (grid[startRow][startCol] === BLACK_CELL) continue;
      if (visited.has(cellKey(startRow, startCol))) continue;
      if (!getEdges(cells[startRow][startCol])) continue;

      const component: [number, number][] = [];
      const queue: [number, number][] = [[startRow, startCol]];
      visited.add(cellKey(startRow, startCol));
      let allTwoEdge = true;

      while (queue.length > 0) {
        const [r, c] = queue.shift()!;
        const edges = getEdges(cells[r][c]);
        const count = [1, 2, 4, 8].filter((d) => edges & d).length;
        if (count !== 2) allTwoEdge = false;
        component.push([r, c]);

        for (const [dir, dr, dc] of DIRS) {
          if (!(edges & dir)) continue;
          const k = cellKey(r + dr, c + dc);
          if (!visited.has(k)) {
            visited.add(k);
            queue.push([r + dr, c + dc]);
          }
        }
      }

      if (allTwoEdge && component.length < totalWhiteCells) {
        // Only this loop is drawn — no stray edges elsewhere on the board.
        const message =
          component.length === totalEdgeCells
            ? 'The river must flow through all white cells.'
            : 'The river must be one single continuous loop.';
        return mkErrorMessage(
          message,
          component.map(([r, c]) => mkErrorCell(r, c))
        );
      }
    }
  }

  return [
    ...(incorrectCells.length > 0 ? mkErrorMessage('These cells are incorrect.', incorrectCells) : []),
    ...(incorrectNotes.length > 0
      ? mkErrorMessage(incorrectNotes.length === 1 ? 'This X is incorrect.' : 'These Xs are incorrect.', incorrectNotes)
      : []),
  ];
}

/** Returns true if the given edge direction on a cell is marked as blocked (has an X note). */
export function checkEdgeBlocked(cells: CellValue[][], row: number, col: number, edge: number): boolean {
  return hasEdgeNote(cells[row]?.[col] ?? 0, edge);
}

/**
 * Returns true if the drawn river passes through every checkpoint in order,
 * starting at checkpoints[0] and ending at checkpoints[last]. Other cells may
 * appear between checkpoints along the path.
 *
 * Works by following the river linearly from the start cell (no backtracking),
 * trying each available outgoing direction. For a closed loop two directions
 * are tried; for a partial path only the forward direction is available.
 */
export function riverPassesThroughInOrder(cells: CellValue[][], checkpoints: [number, number][]): boolean {
  if (checkpoints.length < 2) return true;

  const cellAt = (r: number, c: number): number => cells[r]?.[c] ?? 0;
  const [startR, startC] = checkpoints[0];
  const startEdges = getEdges(cellAt(startR, startC));

  // Follow the river linearly from the start cell in the given initial direction,
  // ticking off checkpoints as they are encountered in order.
  const tryFrom = (initDr: number, initDc: number): boolean => {
    let prevR = startR,
      prevC = startC;
    let curR = startR + initDr,
      curC = startC + initDc;
    let cpIdx = 1;

    while (true) {
      if (curR === checkpoints[cpIdx][0] && curC === checkpoints[cpIdx][1]) {
        if (++cpIdx >= checkpoints.length) return true;
      }

      // Advance to the next cell, never stepping back the way we came.
      let nextR = -1,
        nextC = -1;
      const edges = getEdges(cellAt(curR, curC));
      for (const [dir, dr, dc] of DIRS) {
        if (!(edges & dir)) continue;
        const nr = curR + dr,
          nc = curC + dc;
        if (nr === prevR && nc === prevC) continue;
        nextR = nr;
        nextC = nc;
        break;
      }

      if (nextR === -1) return false; // dead end before finding all checkpoints
      if (nextR === startR && nextC === startC) return false; // looped back without success

      prevR = curR;
      prevC = curC;
      curR = nextR;
      curC = nextC;
    }
  };

  for (const [dir, dr, dc] of DIRS) {
    if (!(startEdges & dir)) continue;
    if (tryFrom(dr, dc)) return true;
  }

  return false;
}
